import type React from 'react';
import { type ReactNode, useCallback, useMemo, useRef } from 'react';

import {
  DREAM_COMPONENTS_CONTEXT_DEFAULT_VALUES,
  DreamComponentsContext,
  type DreamComponentsContextType,
  HighPriorityIndexContext,
  ImageIndexContext,
} from './context.js';

interface DreamComponentsProviderProps {
  children: ReactNode;
  context: DreamComponentsContextType;
}

export const DreamComponentsProvider: React.FC<DreamComponentsProviderProps> = ({ children, context }) => {
  const imageCounterRef = useRef(0);
  const highPriorityCounterRef = useRef(0);
  const getNextImageIndex = useCallback(() => ++imageCounterRef.current, []);
  const getNextHighPriorityIndex = useCallback(() => ++highPriorityCounterRef.current, []);

  const dreamContext = useMemo(() => ({ ...DREAM_COMPONENTS_CONTEXT_DEFAULT_VALUES, ...context }), [context]);

  const imageIndexValue = useMemo(
    () => (context.imagePriorityThreshold ? getNextImageIndex : null),
    [context.imagePriorityThreshold, getNextImageIndex],
  );

  const highPriorityIndexValue = useMemo(
    () => (context.imageHighPriorityCount != null ? getNextHighPriorityIndex : null),
    [context.imageHighPriorityCount, getNextHighPriorityIndex],
  );

  return (
    <DreamComponentsContext.Provider value={dreamContext}>
      <ImageIndexContext.Provider value={imageIndexValue}>
        <HighPriorityIndexContext.Provider value={highPriorityIndexValue}>{children}</HighPriorityIndexContext.Provider>
      </ImageIndexContext.Provider>
    </DreamComponentsContext.Provider>
  );
};
